import React, { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Card, Grid, makeStyles } from '@material-ui/core'
import ColorPickerField from 'components/Form/ColorPickerField'
import { Form, Formik } from 'formik'
import { addIcon, updateIconById, fetchIconById, resetIcon } from 'store/icon'
import { Collection } from 'constants/index'
import { iconFormSchema } from 'schemas/icon'
import ViewHeader from 'components/ViewHeader'
import ProgressButton from 'components/ProgressButton'
import ActionBar from 'components/ActionBar'
import CustomTextField from 'components/Form/CustomTextField'
import AvatarPicker from 'components/AvatarPicker'
import { uploadImageFirstTime } from 'utils'
import { toast } from 'utils/notify'

const EventCardForm = ({ displayContext, onSuccess, onCloseModal }) => {
  const classes = useStyles()
  const { iconId } = useParams()
  const dispatch = useDispatch()
  const history = useHistory()
  const [iconFile, setIconFile] = useState(null)
  const [loading, setLoading] = useState(false)

  const { user } = useSelector((state) => state.auth)
  const { icon } = useSelector((state) => state.icon)

  const initialValues = {
    type: icon?.type ?? '',
    bgColor: icon?.bgColor ?? '#F7F7F7'
  }

  const handleImageChange = (file) => {
    setIconFile(file)
  }

  const goBack = () => {
    dispatch(resetIcon())
    history.goBack()
  }

  useEffect(() => {
    dispatch(fetchIconById(iconId))

    return () => dispatch(resetIcon())
  }, [iconId])

  const onCancel = () => {
    if (displayContext === 'page') {
      goBack()
    } else {
      onCloseModal()
    }
  }

  const handleUpload = async (name) => {
    await dispatch(updateIconById(iconId, { imageName: name }))
  }

  const handleDelete = async () => {
    await dispatch(updateIconById(iconId, { imageName: null }))
  }

  const onSubmit = async ({ type, bgColor }) => {
    setLoading(true)
    const payload = {
      type: type.trim(),
      bgColor: bgColor.trim(),
      conferenceId: user?.conferenceId
    }
    if (iconFile) {
      payload.imageName = await uploadImageFirstTime(
        iconFile,
        Collection.EVENT_CARD_STYLES,
        user?.conferenceId
      )
    }

    let res, message
    if (iconId) {
      res = await dispatch(updateIconById(iconId, payload))
      message = 'Card color updated successfully.'
    } else {
      res = await dispatch(addIcon(payload))
      message = 'Card color added successfully.'
    }
    setLoading(false)
    if (res && displayContext === 'page') {
      toast.success(message)
      goBack()
    }
    displayContext === 'modal' && onSuccess && onSuccess(res)
  }

  return (
    <Card
      elevation={displayContext === 'page' ? 3 : 0}
      className={displayContext === 'page' ? classes.card : classes.styledCard}>
      {displayContext === 'page' && (
        <ViewHeader title={iconId ? 'Edit Card Color' : 'Create Card Color'}>
          <div style={{ marginRight: 10 }}>
            <ActionBar actionText="Back" onAction={() => goBack()} />
          </div>
        </ViewHeader>
      )}
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={iconFormSchema}
        onSubmit={onSubmit}>
        {({ values, setFieldValue }) => (
          <Form>
            <Grid container spacing={3} style={{ marginTop: '0.25rem' }}>
              <Grid item xs={12} md={6}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '12px',
                    color: 'rgba(0,0,0,0.54)',
                    marginBottom: '4px',
                    fontFamily: '"Roboto","Helvetica","Arial",sans-serif'
                  }}>
                  Card Name
                </label>
                <CustomTextField name="type" value={values.type} type="text" />
              </Grid>

              <Grid item xs={12} md={6} lg={3}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '12px',
                    color: 'rgba(0,0,0,0.54)',
                    marginBottom: '4px',
                    fontFamily: '"Roboto","Helvetica","Arial",sans-serif'
                  }}>
                  Card Color
                </label>
                <ColorPickerField
                  value={values.bgColor}
                  onChange={(color) => setFieldValue('bgColor', color)}
                />
              </Grid>

              <Grid item xs={12} lg={6}>
                <AvatarPicker
                  title="Icon Image"
                  subTitle="Allowed Format: png/jpg ; Dimensions: 64 x 64"
                  path={Collection.EVENT_CARD_STYLES}
                  handleCustomChange={handleImageChange}
                  onUpload={handleUpload}
                  onDelete={handleDelete}
                  imageName={iconId ? icon?.imageName : null}
                  conferenceId={user?.conferenceId}
                  firsTimeUpload={!iconId}
                />
              </Grid>
            </Grid>
            <Grid container justifyContent="flex-end" spacing={2}>
              <Grid item>
                <Button size="large" onClick={onCancel} disabled={loading}>
                  Cancel
                </Button>
              </Grid>
              <Grid item>
                <ProgressButton
                  type="submit"
                  label="Save"
                  inProgress={loading}
                  disabled={loading}
                />
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Card>
  )
}
export default EventCardForm

const useStyles = makeStyles({
  card: {
    padding: '24px',
    overflow: 'visible'
  },
  styledCard: {
    paddingTop: '1rem'
  }
})
